// Farbschema-Umschaltung. Dunkel ist der Standard; Hell setzt data-theme="light" auf <html>, worauf
// tokens.css mit dem [data-theme='light']-Block reagiert (die WERTE beider Themes leben dort, nicht hier).
// Dieses Modul kennt nur den Zustand: lesen / anwenden / persistieren / abonnieren.
//
// Neben den festen Modi gibt es „System": folgt prefers-color-scheme des Geräts und reagiert live auf
// dessen Wechsel (matchMedia-Listener). Gespeichert wird die AUSWAHL (dark/light/system), angewendet
// immer der aufgelöste Modus — Umgebungen ohne matchMedia (alte Browser, jsdom) fallen auf Dunkel zurück.
//
// Persistenz-Schlüssel bewusst wie im Alt-Monolithen ('wf_theme'), damit ein Gerät seinen Modus behält.
// Angewendet wird vor dem Render (initTheme in main.tsx) — so gibt es kein Hell/Dunkel-Flackern beim Boot,
// auch ohne Inline-Script (das die harte CSP verböte).

export type Theme = 'dark' | 'light'
export type ThemePick = Theme | 'system'

const STORAGE_KEY = 'wf_theme'
const listeners = new Set<() => void>()

function readStored(): ThemePick {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'light' || raw === 'system' ? raw : 'dark'
  } catch {
    return 'dark' // localStorage gesperrt (Privatmodus): still auf den Standard zurückfallen, nie werfen.
  }
}

function systemTheme(): Theme {
  try {
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
    }
  } catch {
    /* matchMedia nicht verfügbar → Standard */
  }
  return 'dark'
}

function resolve(pick: ThemePick): Theme {
  return pick === 'system' ? systemTheme() : pick
}

function applyToDocument(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'light') root.setAttribute('data-theme', 'light')
  else root.removeAttribute('data-theme') // kein data-theme = Dunkel (der :root-Default in tokens.css)
}

let currentPick: ThemePick = readStored()

// Bei „System" auf den Geräte-Wechsel reagieren (z. B. Auto-Dunkel am Abend). Ein einziger Listener,
// registriert beim Boot; wirkt nur, solange die Auswahl 'system' ist.
function watchSystem(): void {
  try {
    if (typeof window.matchMedia !== 'function') return
    window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
      if (currentPick !== 'system') return
      applyToDocument(systemTheme())
      for (const listener of listeners) listener()
    })
  } catch {
    /* ohne matchMedia kein Live-Wechsel — der feste Fallback (Dunkel) gilt weiter */
  }
}
let watching = false

/** Beim Boot aufrufen (vor dem Render): gespeicherte Auswahl auf <html> anwenden. */
export function initTheme(): void {
  currentPick = readStored()
  applyToDocument(resolve(currentPick))
  if (!watching) {
    watching = true
    watchSystem()
  }
}

/** Aufgelöster Modus (bei „System" der aktuelle Geräte-Modus) — für alle, die nur dunkel/hell brauchen. */
export function getTheme(): Theme {
  return resolve(currentPick)
}

/** Die gespeicherte AUSWAHL (kann 'system' sein) — für die Anzeige im Umschalter. */
export function getThemePick(): ThemePick {
  return currentPick
}

export function setTheme(pick: ThemePick): void {
  currentPick = pick
  applyToDocument(resolve(pick))
  // Kurzer Crossfade statt hartem Umschlag: die Klasse aktiviert eine Farb-Transition in styles.css
  // und verschwindet danach wieder — sonst würde JEDE spätere Interaktion träge nachziehen.
  const root = document.documentElement
  root.classList.add('wf-theming')
  setTimeout(() => root.classList.remove('wf-theming'), 320)
  try {
    localStorage.setItem(STORAGE_KEY, pick)
  } catch {
    // Persistenz nicht verfügbar — der Modus gilt dann nur für diese Sitzung. Kein Absturz.
  }
  for (const listener of listeners) listener()
}

export function toggleTheme(): void {
  setTheme(getTheme() === 'light' ? 'dark' : 'light')
}

/** Abonnement für React (useSyncExternalStore in useThemePick). Gibt die Abmeldefunktion zurück. */
export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
